/**
 * --------------------------------------------------------------------------
 * Cart Line Items Manager
 * --------------------------------------------------------------------------
 * Manages dynamic rendering and updates of cart line items.
 * Syncs with optimistic cart system to show real-time changes.
 */

(function () {
  'use strict';

  const SELECTORS = {
    CONTAINER: '[data-line-items-container]',
    LINE_ITEM: '[data-line-item-variant-id]',
    EMPTY_MESSAGE: '[data-empty-cart-message]',
    REMOVE_BUTTON: '[data-remove-item]',
  };

  let isInitialized = false;

  // Cache of variant_id -> compare_at_price (in cents). Populated by
  // fetching /products/{handle}.js for each unique handle in the cart,
  // since /cart.js does not include compare_at_price on line items.
  const compareAtPriceByVariantId = new Map();
  const productFetchPromises = new Map(); // handle -> Promise

  function getCompareAtPriceForVariant(variantId) {
    if (variantId === undefined || variantId === null) return null;
    const cached = compareAtPriceByVariantId.get(String(variantId));
    return cached || null;
  }

  function ensureProductDataForHandle(handle) {
    if (!handle) return Promise.resolve();
    if (productFetchPromises.has(handle)) {
      return productFetchPromises.get(handle);
    }
    const p = fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(product => {
        if (!product || !Array.isArray(product.variants)) return;
        product.variants.forEach(v => {
          if (v.compare_at_price != null) {
            // /products/{handle}.js returns compare_at_price in cents (number).
            const cents = typeof v.compare_at_price === 'number'
              ? v.compare_at_price
              : Math.round(parseFloat(v.compare_at_price) * 100);
            if (!Number.isNaN(cents)) {
              compareAtPriceByVariantId.set(String(v.id), cents);
            }
          }
        });
      })
      .catch(() => {});
    productFetchPromises.set(handle, p);
    return p;
  }


  /**
   * Creates a line item HTML element from cart item data
   */
  function createLineItemElement(item) {
    const div = document.createElement('div');
    div.className = 'flex gap-[12px] items-center overflow-clip relative shrink-0 w-full';
    div.setAttribute('data-line-item-variant-id', item.variant_id || item.id);

    // Check if this is a cart page (for responsive text sizing)
    const container = document.querySelector(SELECTORS.CONTAINER);
    const isCartPage = container?.hasAttribute('data-cart-page');
    const textSizeClass = isCartPage ? 'text-sm md:text-base' : 'text-sm';

    // Extract data - use line prices (total for quantity) not single item prices
    const finalPrice = item.final_line_price || item.line_price || 0;
    let originalPrice = item.original_line_price || item.original_price;
    let hasDiscount = originalPrice && originalPrice > finalPrice;

    // Shopify's /cart.js does NOT expose variant.compare_at_price, but the
    // PDP shows the compare-at price crossed out when it's higher than the
    // variant price. Use cached product data (populated in updateLineItems)
    // to mirror that strikethrough in the cart modal.
    const variantId = item.variant_id || item.id;
    const compareAtPrice = getCompareAtPriceForVariant(variantId);
    if (compareAtPrice && compareAtPrice > (item.price || 0)) {
      const compareAtLinePrice = compareAtPrice * (item.quantity || 1);
      if (compareAtLinePrice > (originalPrice || 0)) {
        originalPrice = compareAtLinePrice;
        hasDiscount = true;
      }
    }
    const variantTitle = item.variant_title || '';
    
    // Get product title
    let productTitle = item.product_title || '';
    if (!productTitle && item.title) {
      productTitle = item.title;
      if (variantTitle && productTitle.includes(' - ')) {
        const variantSuffix = ` - ${variantTitle}`;
        if (productTitle.endsWith(variantSuffix)) {
          productTitle = productTitle.slice(0, -variantSuffix.length);
        }
      }
    }
    
    const rawImageUrl = item.image || item.featured_image?.url || item.featured_image || '';
    const imageUrl = window.theme?.utils?.imageUrl 
      ? window.theme.utils.imageUrl(rawImageUrl, { width: 144, height: 144 })
      : rawImageUrl;

    const formatMoney = (cents) => {
      const pounds = cents / 100;
      if (window.theme?.utils?.money) {
        return window.theme.utils.money(pounds);
      }
      return '£' + pounds.toFixed(2);
    };

    // Determine if this is a loading state (missing essential data)
    const isLoadingState = !item || !productTitle || finalPrice === undefined || finalPrice === null;
    const containerClass = isLoadingState ? 
      'flex gap-[12px] items-center overflow-clip relative shrink-0 w-full animate-pulse' : 
      'flex gap-[12px] items-center overflow-clip relative shrink-0 w-full';

    div.className = containerClass;

    div.innerHTML = `
      <div class="flex flex-row items-center self-stretch">
        <div class="h-full relative shrink-0 w-[72px]">
          ${imageUrl ? `
          <img src="${imageUrl}" 
               loading="lazy" 
               class="block max-w-none size-full object-cover" 
               width="72" 
               height="72" 
               alt="${productTitle}">
          ` : `
          <div class="block size-full rounded-md bg-[#F2F2F2]"></div>
          `}
        </div>
      </div>
      <div class="basis-0 flex flex-col gap-[12px] grow items-start justify-center min-h-px min-w-px relative shrink-0">
        <div class="flex items-center relative shrink-0 w-full">
          <div class="flex flex-col items-start relative shrink-0 ${textSizeClass}">
            <div class="flex flex-col items-start leading-[0] not-italic relative shrink-0 text-nowrap">
              <div class="flex flex-col justify-end relative shrink-0 text-black">
                ${productTitle ? `
                <p class="leading-[1.1] text-nowrap whitespace-pre font-medium">${productTitle}</p>
                ` : `
                <div class="h-4 w-32 rounded-md bg-gray-200 blur-sm overflow-hidden">
                  <p class="text-xs text-gray-400 blur-[2px] select-none">Lorem ipsum dolor sit</p>
                </div>
                `}
              </div>
              ${variantTitle && variantTitle.trim() ? `
              <div class="flex flex-col justify-end relative shrink-0 text-[rgba(0,0,0,0.45)]">
                <p class="leading-[1.1] text-nowrap whitespace-pre">${variantTitle}</p>
              </div>
              ` : isLoadingState ? `
              <div class="flex flex-col justify-end relative shrink-0 mt-1">
                <div class="h-3 w-20 rounded-md bg-gray-200 blur-sm overflow-hidden">
                  <p class="text-xs text-gray-400 blur-[2px] select-none">Lorem ipsum</p>
                </div>
              </div>
              ` : ''}
            </div>
            <div class="flex gap-1 items-start leading-[0] not-italic relative shrink-0 text-nowrap">
              ${finalPrice !== undefined && finalPrice !== null ? (hasDiscount && originalPrice ? `
              <div class="flex flex-col justify-end relative shrink-0 color-[#c90016]">
                <p class="leading-[1.1] text-nowrap whitespace-pre font-medium text-[#C90016]">${formatMoney(finalPrice)}</p>
              </div>
              <div class="flex flex-col font-['Grotek:Regular',sans-serif] justify-end relative shrink-0 color-[0px] text-black">
                <p class="[text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid leading-[1.1] line-through not-italic text-nowrap whitespace-pre">${formatMoney(originalPrice)}</p>
              </div>
              ` : `
              <div class="flex flex-col justify-end leading-[0] not-italic relative shrink-0 text-base text-black text-nowrap">
                <p class="leading-[1.1] font-medium whitespace-pre">${formatMoney(finalPrice)}</p>
              </div>
              `) : `
              <div class="flex flex-col justify-end leading-[0] not-italic relative shrink-0 text-base">
                <div class="h-4 w-16 rounded-md bg-gray-200 blur-sm overflow-hidden">
                  <p class="text-xs text-gray-400 blur-[2px] select-none">£00.00</p>
                </div>
              </div>
              `}
            </div>
          </div>
        </div>
        <div class="flex items-center justify-between relative shrink-0 w-full">
          ${isLoadingState ? `
          <div class="bg-gray-200 box-border flex items-stretch justify-center gap-1 relative rounded-full shrink-1 grow-0 h-[34px] w-[90px] animate-pulse">
          </div>
          <div class="px-4 h-[34px] relative shrink-0 flex items-center justify-center bg-gray-200 rounded animate-pulse w-[34px]">
          </div>
          ` : `
          <cart-quantity-input class="bg-[#f2f2f2] box-border flex items-stretch justify-center gap-1 relative rounded-full shrink-1 grow-0">
            <button
              type="button"
              class="shrink-0 pl-4 h-[34px] flex items-center justify-center cursor-pointer appearance-none bg-transparent border-0"
              data-value="-1"
              aria-label="Decrease quantity"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="1" fill="none" viewBox="0 0 12 1"><path fill="#000" d="M12 0v1H0V0z"/></svg>
            </button>
            <input
              type="number"
              name="quantity"
              value="${item.quantity || 0}"
              min="0"
              data-variant-id="${item.variant_id || item.id}"
              class="flex h-[34px] w-[31px] relative shrink-0 text-base text-black text-nowrap appearance-none bg-transparent border-0 text-center p-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none pointer-events-none"
              aria-label="Quantity"
              readonly
            />
            <button
              type="button"
              class="relative shrink-0 pr-4 flex items-center justify-center cursor-pointer appearance-none bg-transparent border-0"
              data-value="1"
              aria-label="Increase quantity"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" viewBox="0 0 13 13"><path fill="#000" d="M6.798 5.694H12.5v1.112H6.798V12.5H5.687V6.806H0V5.694h5.687V0h1.11z"/></svg>
            </button>
          </cart-quantity-input>
          <button
            type="button"
            class="px-4 h-[34px] relative shrink-0 flex items-center justify-center cursor-pointer appearance-none bg-transparent border-0"
            data-remove-item="${item.variant_id || item.id}"
            aria-label="Remove ${productTitle || 'item'}"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" fill="none" viewBox="0 0 18 20"><path fill="#000" d="m15.573 7.338-.613 9.5v.003c-.025.361-.052.75-.124 1.108-.073.365-.201.74-.454 1.069-.527.685-1.435.982-2.808.982H5.518c-1.373 0-2.282-.297-2.808-.982-.253-.33-.38-.704-.454-1.069-.072-.358-.099-.747-.124-1.108v-.003l-.613-9.5 1.13-.072.613 9.499.04.52q.024.237.064.44c.054.268.132.46.242.603.2.26.65.54 1.91.54h6.056c1.26 0 1.71-.28 1.91-.54.11-.143.188-.334.242-.602.055-.274.078-.586.104-.964l.613-9.496zm-5.46 7.474H6.97v-1.133h3.141zm.791-3.775H6.188V9.905h4.716zM9.781 0c.865 0 1.497.19 1.903.657.37.428.45.996.514 1.39l.208 1.226-.62.105q2.657.117 5.306.379l-.111 1.127a95 95 0 0 0-9.397-.469q-2.774 0-5.547.28h-.002L.11 4.884 0 3.757l1.925-.189a57 57 0 0 1 3.153-.228l-.392-.066.208-1.236c.067-.403.147-.975.521-1.4C5.822.179 6.453 0 7.31 0zM7.31 1.132c-.738 0-.96.157-1.046.255-.12.134-.171.345-.254.839l-.183 1.086a56 56 0 0 1 1.757-.029q1.843 0 3.688.072l-.19-1.12v-.003c-.077-.473-.131-.691-.254-.833-.09-.103-.318-.267-1.047-.267z"/></svg>
          </button>
          `}
        </div>
      </div>
    `;

    return div;
  }


  /**
   * Updates line items in the DOM
   * Handles multiple containers on the same page
   */
  function updateLineItems(cart) {
    const containers = document.querySelectorAll(SELECTORS.CONTAINER);
    if (!containers.length) return;

    const items = cart?.items || [];

    // Kick off (or reuse cached) product fetches for any handles we haven't
    // seen yet. When new data arrives, re-render so the strikethrough appears.
    const handles = Array.from(new Set(items.map(i => i.handle).filter(Boolean)));
    const unknownHandles = handles.filter(h => !productFetchPromises.has(h));
    if (unknownHandles.length) {
      Promise.all(unknownHandles.map(ensureProductDataForHandle)).then(() => {
        // Re-render with populated compare-at cache.
        renderItems(items, containers);
      });
    }

    renderItems(items, containers);
  }

  function renderItems(items, containers) {
    // Update each container independently
    containers.forEach(container => {
      const emptyCartMessage = container.querySelector(SELECTORS.EMPTY_MESSAGE);

      // Clear all existing line items in this container
      const existingLineItems = container.querySelectorAll(SELECTORS.LINE_ITEM);
      existingLineItems.forEach(element => element.remove());

      // Render all cart items fresh in this container
      items.forEach(item => {
        const lineItemElement = createLineItemElement(item);
        
        if (emptyCartMessage) {
          container.insertBefore(lineItemElement, emptyCartMessage);
        } else {
          container.appendChild(lineItemElement);
        }
      });

      // Handle empty cart message for this container
      const hasItems = items.length > 0;
      if (hasItems) {
        if (emptyCartMessage) {
          emptyCartMessage.style.display = 'none';
        }
      } else {
        if (!emptyCartMessage) {
          const emptyMsg = document.createElement('div');
          emptyMsg.setAttribute('data-empty-cart-message', '');
          emptyMsg.className = 'flex items-center justify-center py-8 text-center';
          emptyMsg.innerHTML = '<p class="text-base text-black">Your cart is empty</p>';
          container.appendChild(emptyMsg);
        } else {
          emptyCartMessage.style.display = 'flex';
        }
      }
    });
  }

  /**
   * Handles delete button clicks
   */
  async function handleDeleteClick(e) {
    const deleteButton = e.target.closest(SELECTORS.REMOVE_BUTTON);
    if (!deleteButton) return;

    e.preventDefault();
    e.stopPropagation();

    const variantId = deleteButton.getAttribute('data-remove-item');
    if (!variantId) return;

    if (window.Shopify && window.Shopify.cart) {
      try {
        await window.Shopify.cart.remove(variantId);
      } catch (error) {
        console.error('Failed to remove item from cart:', error);
        // Refresh cart state on error
        window.Shopify.cart.get().then(updateLineItems).catch(console.error);
      }
    }
  }

  /**
   * Initialize
   */
  function initialize() {
    if (isInitialized) return;

    const containers = document.querySelectorAll(SELECTORS.CONTAINER);
    if (!containers.length) return;

    isInitialized = true;

    // Set up delete button click handler
    document.addEventListener('click', handleDeleteClick);

    // Listen for cart updates
    window.addEventListener('cart:updated', function(event) {
      const cart = event.detail?.cart;
      if (cart) {
        updateLineItems(cart);
      } else if (window.Shopify && window.Shopify.cart) {
        window.Shopify.cart.get().then(updateLineItems).catch(console.error);
      }
    });


    // Initial update on page load
    if (window.Shopify && window.Shopify.cart) {
      window.Shopify.cart.ensureInitialized().then(() => {
        window.Shopify.cart.get().then(updateLineItems).catch(console.error);
      });
    }
  }

  // Initialize when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Retry after delay if not initialized
  setTimeout(() => {
    if (!isInitialized) {
      initialize();
    }
  }, 100);

})();
